import { useEffect, useState } from "react";
import { useReducedMotion } from "./useReducedMotion.js";
import type { MotionPhase } from "../motion.js";

export interface TransitionPresence {
  readonly present: boolean;
  readonly phase: MotionPhase;
}

/**
 * Retains exiting UI long enough for its CSS transition to finish.
 *
 * Logical state changes immediately: callers must disable interaction and ARIA
 * exposure while `phase === "closing"`. Reduced-motion users never wait for a
 * decorative exit before the element is removed.
 */
export function useTransitionPresence(
  open: boolean,
  exitDurationMs: number,
): TransitionPresence {
  const reducedMotion = useReducedMotion();
  const [state, setState] = useState<
    TransitionPresence & { open: boolean; reducedMotion: boolean }
  >({
    open,
    reducedMotion,
    present: open,
    phase: open && !reducedMotion ? "opening" : open ? "open" : "closed",
  });

  // A prop change is a state-machine event, not an external synchronization.
  // Deriving it during render avoids an extra painted frame in which a closed
  // overlay is still interactive. React immediately retries this component
  // with the new snapshot before committing it.
  if (state.open !== open || state.reducedMotion !== reducedMotion) {
    setState({
      open,
      reducedMotion,
      present: open || (!reducedMotion && state.present),
      phase: open
        ? reducedMotion
          ? "open"
          : "opening"
        : reducedMotion || !state.present
          ? "closed"
          : "closing",
    });
  }

  useEffect(() => {
    let frame = 0;
    let timeout = 0;

    if (state.phase === "opening") {
      frame = window.requestAnimationFrame(() =>
        setState((current) =>
          current.phase === "opening" ? { ...current, phase: "open" } : current,
        ),
      );
    } else if (state.phase === "closing") {
      timeout = window.setTimeout(
        () =>
          setState((current) =>
            current.phase === "closing"
              ? { ...current, present: false, phase: "closed" }
              : current,
          ),
        exitDurationMs,
      );
    }

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [exitDurationMs, state.phase]);

  return { present: state.present, phase: state.phase };
}
